import { useEffect, useMemo, useRef, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { ChartOfAccountsView } from "@common/types/views/chartOfAccountsView";
import { AccountGroup } from "@common/types/domains/accounting/account";
import type { PreviewAccountOrigin } from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import { ChartOfAccountsOrderMode } from "@common/helpers/domains/accounting/buildHierarchicalChartOfAccounts";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import {
    buildHierarchicalChartOfAccounts,
    getDefaultExpandedState,
} from "@components/accounting/chartOfAccounts/utils";
import type {
    ApplyCoaPreviewTableMeta,
    PreviewOriginEntity,
} from "./cells/ApplyCoaPreviewNameCell";
import type { PreviewExtendedAccount } from "./applyCoaTemplatePreviewMapper";
import { CoaPreviewMode } from "./applyCoaPreviewTagCopy";
import {
    buildApplyCoaPreviewDefaultView,
    createApplyCoaPreviewViewConfig,
    resolveChartOfAccountsView,
} from "./applyCoaTemplatePreviewViewConfig";
import { ApplyCoaPreviewFilters } from "./ApplyCoaPreviewFilters";
import {
    ApplyCoaPreviewOriginSource,
    ApplyCoaPreviewSource,
    ApplyCoaPreviewUpdate,
    matchesOrigin,
    matchesSource,
    matchesUpdate,
} from "./applyCoaTemplatePreviewStatus";
import styles from "./ApplyCoaPreviewTable.module.scss";

interface Props {
    accounts: PreviewExtendedAccount[];
    groups: AccountGroup[];
    // Ids of groups this view treats as newly added (drives the group "Added"
    // tag). Empty when nothing was added.
    newGroupIds: Set<string>;
    groupOriginById?: Map<string, PreviewAccountOrigin>;
    // "preview" = live wizard apply (future tense, template<>entity diff);
    // "historical" = Activity drawer (past tense, stored change set only).
    mode: CoaPreviewMode;
    // The previewed company, enabling the Origin column/filter. Only meaningful
    // in preview mode; the historical drawer never passes it.
    originEntity?: PreviewOriginEntity;
}

// Presentational CoA tree/table shared by the live apply preview and the
// historical Activity drawer. The wizard feeds it a template<>entity diff; the
// drawer feeds it a stored change set via the historical adapter. Historical
// mode hides Origin, but keeps the accurate Source column.
export const ApplyCoaPreviewTable = ({
    accounts,
    groups,
    newGroupIds,
    groupOriginById,
    mode,
    originEntity,
}: Props) => {
    const showAccountNumbers = useShowAccountNumbers();
    const isPreview = mode === "preview";
    const showOriginColumn = isPreview && originEntity != null;

    const [search, setSearch] = useState<string | undefined>(undefined);
    const [originSources, setOriginSources] = useState<
        ApplyCoaPreviewOriginSource[]
    >([]);
    const [updates, setUpdates] = useState<ApplyCoaPreviewUpdate[]>([]);
    const [source, setSource] = useState<ApplyCoaPreviewSource>(
        ApplyCoaPreviewSource.all,
    );

    const viewConfig = useMemo(
        () => createApplyCoaPreviewViewConfig({ showOriginColumn }),
        [showOriginColumn],
    );

    const defaultView = useMemo(
        () => buildApplyCoaPreviewDefaultView({ showOriginColumn }),
        [showOriginColumn],
    );

    const [view, setView] = useState<ChartOfAccountsView>(defaultView);

    const filteredAccounts = useMemo(
        () =>
            accounts.filter(
                (account) =>
                    matchesOrigin(account, originSources) &&
                    matchesUpdate({ account, updates, newGroupIds, mode }) &&
                    matchesSource(account, source),
            ),
        [accounts, originSources, updates, source, newGroupIds, mode],
    );

    const hierarchicalData = useMemo(
        () =>
            buildHierarchicalChartOfAccounts({
                accounts: filteredAccounts,
                groups,
                filters: { search },
                orderMode: ChartOfAccountsOrderMode.sortOrder,
                showAccountNumbers,
                withPredefinedSections: true,
                // Origin/Updates/Source are applied to `filteredAccounts` (not
                // via `filters`), so tell the builder a filter is active to
                // prune groups that lost all their accounts.
                hasExternalFilterActive:
                    originSources.length > 0 ||
                    updates.length > 0 ||
                    source !== ApplyCoaPreviewSource.all,
            }),
        [
            filteredAccounts,
            groups,
            search,
            showAccountNumbers,
            originSources,
            updates,
            source,
        ],
    );

    const tableMeta: ApplyCoaPreviewTableMeta = useMemo(
        () => ({
            accountMetaById: new Map(
                accounts.map((account) => [
                    account.id,
                    {
                        mergeStatus: account.mergeStatus,
                        previousCode: account.previousCode,
                        changes: account.changes,
                        previousGroupPath: account.previousGroupPath,
                        origin: account.origin,
                        wasArchivedByApply: account.wasArchivedByApply,
                    },
                ]),
            ),
            originEntity,
            newGroupIds,
            groupOriginById,
            mode,
        }),
        [accounts, originEntity, newGroupIds, groupOriginById, mode],
    );

    const [expanded, setExpanded] = useState<ExpandedState>({});
    const hasInitializedExpandedRef = useRef(false);
    const tableRef = useRef<ViewTableHandle>(null);

    useEffect(() => {
        if (
            hasInitializedExpandedRef.current ||
            hierarchicalData.length === 0
        ) {
            return;
        }
        hasInitializedExpandedRef.current = true;
        setExpanded(getDefaultExpandedState(hierarchicalData));
    }, [hierarchicalData]);

    return (
        <div className={styles.root}>
            <TablePageHeader
                isFullPage
                search={search}
                onSearchChange={(val) => setSearch(val || undefined)}
                afterFiltersChildren={
                    <ApplyCoaPreviewFilters
                        originSources={originSources}
                        onOriginSourcesChange={setOriginSources}
                        originEntity={originEntity}
                        updates={updates}
                        onUpdatesChange={setUpdates}
                        source={source}
                        onSourceChange={setSource}
                        showOrigin={showOriginColumn}
                    />
                }
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <ColumnSettings
                    view={view}
                    viewConfig={viewConfig}
                    defaultView={defaultView}
                    onViewChange={setView}
                />
            </TablePageHeader>
            <div className={styles.tableContainer}>
                <ViewTable
                    ref={tableRef}
                    data={hierarchicalData}
                    view={view}
                    config={viewConfig}
                    tableMeta={tableMeta}
                    onViewChange={(newView) =>
                        setView((currentView) =>
                            resolveChartOfAccountsView(newView, currentView),
                        )
                    }
                    enableColumnResizing={false}
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                    rowBorders={false}
                    nestingIndentPx={16}
                    emptyView={() => (
                        <EmptyState header="No accounts for the selected filters" />
                    )}
                />
            </div>
        </div>
    );
};
